// Vue SESSIONS + lanceur CLI partagés du plugin cli.
// Le CliLauncher est PARAMÉTRÉ par `cli` : en mode mono-CLI (panel dédié), il
// n'affiche pas la grille des 7 CLIs mais un bouton « Démarrer {label} » + la
// sonde d'installation pour CE CLI seulement.
import { Show, For, createSignal, createEffect, type Component } from "solid-js";
import { useToolClient } from "@/shared/tool-client";
import { Collapsible } from "@/shared/collapsible";
import type { CliSession, CliTokenMode, InstallInfo } from "./types";

// ── Session selector (toolbar de chips) ───────────────────────────────────────

interface SessionSelectorProps {
  sessions: CliSession[];
  activeId: string;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
}

export const SessionSelector: Component<SessionSelectorProps> = (props) => (
  <div class="cli-session-selector">
    <For each={props.sessions}>
      {(s) => (
        <div
          class="cli-session-chip"
          data-active={props.activeId === s.sessionId || undefined}
          onClick={() => props.onSelect(s.sessionId)}
        >
          <span class="cli-session-chip-label">{s.cli || s.command || s.sessionId.slice(0, 8)}</span>
          <Show when={s.mode}>
            <span class="cli-session-chip-badge">{s.mode}</span>
          </Show>
          <span class="cli-session-chip-state" data-state={s.status || "running"}>
            {s.status === "exited" ? `exit ${s.exitCode ?? ""}` : "running"}
          </span>
          <button
            class="cli-session-chip-close"
            onClick={(e) => {
              e.stopPropagation();
              props.onClose(s.sessionId);
            }}
            title="Fermer la session"
          >
            ✕
          </button>
        </div>
      )}
    </For>
  </div>
);

// ── Lanceur CLI structuré (cli_check → cli_start) ─────────────────────────────

function tokenModeTitle(value: CliTokenMode): string {
  switch (value) {
    case "off":
      return "Aucun mode token saver";
    case "hybrid":
      return "Consigne compacte + RTK quand disponible";
    case "rtk":
      return "Demande d'utiliser RTK pour les commandes bruyantes";
    case "skill":
      return "Consigne compacte sans RTK";
  }
}

export interface CliLauncherProps {
  client: ReturnType<typeof useToolClient>;
  onStartCli: (cli: string, cwd: string, tokenMode: CliTokenMode) => void;
  startingCli: string;
  currentCwd: () => string;
  /** Si fourni, le launcher se restreint à CE CLI (panel dédié) :
   *  bouton unique « Démarrer {label} » + sonde pour ce CLI seul.
   *  Si null/undefined → comportement historique : grille de tous les CLIs. */
  cli?: string;
  /** Libellé affiché quand `cli` est fourni (ex. « Claude Code »). */
  cliLabel?: string;
}

export const CliLauncher: Component<CliLauncherProps> = (props) => {
  const [clis, setClis] = createSignal<InstallInfo[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [err, setErr] = createSignal("");
  // cwd local du launcher, initialisé/synchronisé sur le cwd partagé du panel
  // (sélection d'un workspace). L'opérateur peut toujours le surcharger à la main.
  const [cwd, setCwd] = createSignal(props.currentCwd());
  const [cwdTouched, setCwdTouched] = createSignal(false);
  const [tokenMode, setTokenMode] = createSignal<CliTokenMode>("off");
  createEffect(() => {
    const shared = props.currentCwd();
    if (!cwdTouched()) setCwd(shared);
  });

  async function check() {
    setLoading(true);
    setErr("");
    try {
      const r = (await props.client.call("cli_check")) as { clis?: InstallInfo[] };
      let rows = (r.clis || []).slice();
      // En mode mono-CLI : on ne garde que le CLI dédié (ou tous si absent de la sonde).
      if (props.cli) {
        const mine = rows.filter((c) => c.id === props.cli);
        if (mine.length > 0) rows = mine;
        else
          rows = [
            { id: props.cli, label: props.cliLabel || props.cli, installed: false, version: null, transport: "?" },
          ];
      }
      // Installés d'abord, puis tri alpha.
      rows.sort((a, b) => {
        if (a.installed !== b.installed) return a.installed ? -1 : 1;
        return (a.label || a.id).localeCompare(b.label || b.id);
      });
      setClis(rows);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // En mode mono-CLI, on sonde automatiquement au montage pour renseigner le badge.
  createEffect(() => {
    if (props.cli && clis().length === 0) void check();
  });

  const headerLabel = () =>
    props.cli ? `🤖 Démarrer ${props.cliLabel || props.cli}` : "🤖 Lancer un CLI IA (structuré)";

  return (
    <Collapsible variant="tool" class="cli-launcher" header={<span>{headerLabel()}</span>}>
      <div class="cli-launcher-body">
        <div class="cli-toolbar">
          <input
            class="panel-filter-input"
            value={cwd()}
            placeholder="Répertoire de travail (cwd) — optionnel…"
            autocomplete="off"
            spellcheck={false}
            onInput={(e) => {
              setCwdTouched(true);
              setCwd(e.currentTarget.value);
            }}
          />
          <button class="btn btn--sm" onClick={() => void check()} disabled={loading()}>
            {loading() ? "Détection…" : props.cli ? "Revérifier" : "Détecter les CLIs"}
          </button>
        </div>

        <div class="cli-token-mode" role="group" aria-label="Mode economie tokens">
          <For
            each={
              [
                ["off", "Off"],
                ["skill", "Skill"],
                ["rtk", "RTK"],
                ["hybrid", "Hybrid"],
              ] as Array<[CliTokenMode, string]>
            }
          >
            {([value, label]) => (
              <button
                type="button"
                class="btn btn--xs btn--ghost cli-token-mode-btn"
                data-active={tokenMode() === value || undefined}
                onClick={() => setTokenMode(value)}
                title={tokenModeTitle(value)}
              >
                {label}
              </button>
            )}
          </For>
        </div>

        <Show when={err()}>
          <div class="panel-error">{err()}</div>
        </Show>

        <Show when={clis().length > 0}>
          <div class="cli-launcher-list">
            <For each={clis()}>
              {(c) => (
                <div class="cli-launcher-row" data-installed={c.installed || undefined}>
                  <span class="cli-launcher-name">{c.label || c.id}</span>
                  <span class="cli-launcher-badge" data-installed={c.installed || undefined}>
                    {c.installed ? "installé" : "absent"}
                  </span>
                  <Show when={c.version}>
                    <span class="cli-launcher-version">{c.version}</span>
                  </Show>
                  <span class="cli-launcher-transport">{c.transport}</span>
                  <button
                    class="btn btn--xs"
                    disabled={!c.installed || props.startingCli === c.id}
                    onClick={() => props.onStartCli(c.id, cwd().trim(), tokenMode())}
                    title={c.installed ? `Démarrer ${c.label}` : "CLI non installé sur le host"}
                  >
                    {props.startingCli === c.id ? "Démarrage…" : "Démarrer"}
                  </button>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
    </Collapsible>
  );
};

// ── Vue SESSIONS (liste + démarrage) ──────────────────────────────────────────

export interface SessionsViewProps {
  sessions: CliSession[];
  activeId: string;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onOpenPty: (command: string) => void;
  onStartCli: (cli: string, cwd: string, tokenMode: CliTokenMode) => void;
  client: ReturnType<typeof useToolClient>;
  opening: boolean;
  startingCli: string;
  currentCwd: () => string;
  /** Restreint le launcher à un CLI dédié (panel par CLI). */
  cli?: string;
  cliLabel?: string;
}

export const SessionsView: Component<SessionsViewProps> = (props) => {
  const [command, setCommand] = createSignal("");

  return (
    <div class="cli-sessions">
      <CliLauncher
        client={props.client}
        onStartCli={props.onStartCli}
        startingCli={props.startingCli}
        currentCwd={props.currentCwd}
        cli={props.cli}
        cliLabel={props.cliLabel}
      />

      <div class="cli-toolbar">
        <input
          class="panel-filter-input"
          value={command()}
          placeholder="Commande PTY (ex: pwsh, bash, claude)…"
          autocomplete="off"
          spellcheck={false}
          onInput={(e) => setCommand(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && command().trim() && props.onOpenPty(command().trim())}
        />
        <button
          class="btn btn--sm"
          onClick={() => command().trim() && props.onOpenPty(command().trim())}
          disabled={props.opening || !command().trim()}
        >
          {props.opening ? "Ouverture…" : "+ Terminal PTY"}
        </button>
      </div>

      <Show when={props.sessions.length > 0} fallback={<div class="panel-empty">Aucune session CLI ouverte.</div>}>
        <div class="cli-sessions-list">
          <For each={props.sessions}>
            {(s) => (
              <div
                class="cli-sessions-row"
                data-active={props.activeId === s.sessionId || undefined}
                onClick={() => props.onSelect(s.sessionId)}
              >
                <span class="cli-sessions-cli">{s.cli || s.command || "—"}</span>
                <span class="cli-sessions-mode">
                  {s.tokenMode && s.tokenMode !== "off" ? `${s.mode || "?"} · ${s.tokenMode}` : s.mode || "?"}
                </span>
                <span class="cli-sessions-cwd" title={s.cwd}>
                  {s.cwd || ""}
                </span>
                <Show when={s.pid}>
                  <span class="cli-sessions-pid">pid {s.pid}</span>
                </Show>
                <span class="cli-sessions-state" data-state={s.status || "running"}>
                  {s.status === "exited" ? `exit ${s.exitCode ?? ""}` : "running"}
                </span>
                <button
                  class="btn btn--xs btn--ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onClose(s.sessionId);
                  }}
                >
                  Fermer
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};
